import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    FileText, CalendarClock, MapPin, ArrowRight, AlertTriangle, CalendarCheck, UserCircle2, ListChecks,
} from "lucide-react";
import { api, formatApiError, useAuth, API } from "@/lib/api";
import { toast } from "sonner";

function Card({ icon: Icon, title, value, subtitle, to, ctaLabel, testId }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="flex h-full flex-col rounded-2xl border border-brand-line bg-white p-6 shadow-premium"
            data-testid={testId}
        >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                <Icon className="h-5 w-5" />
            </div>
            <div className="mt-5 font-display text-4xl text-brand-ink leading-none">{value}</div>
            <div className="mt-2 text-sm font-semibold text-brand-ink">{title}</div>
            <div className="mt-1 text-xs text-brand-smoke">{subtitle}</div>
            <Link
                to={to}
                className="mt-6 inline-flex items-center gap-2 self-start rounded-full border border-brand-line px-4 py-2 text-xs font-semibold hover:border-brand-orange hover:text-brand-orange"
            >
                {ctaLabel}
                <ArrowRight className="h-3.5 w-3.5" />
            </Link>
        </motion.div>
    );
}

function hasDisposNextThreeMonths(slots) {
    if (!slots || slots.length === 0) return false;
    const today = new Date();
    const limit = new Date();
    limit.setMonth(limit.getMonth() + 3);
    return slots.some((s) => {
        const d = new Date(s.date);
        return d >= today && d <= limit;
    });
}

export default function FormateurDashboard() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [dispos, setDispos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/formateur/me"),
            api.get("/formateur/disponibilites"),
        ])
            .then(([m, d]) => {
                setSummary(m.data);
                setDispos(d.data?.slots || []);
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    const hasNear = hasDisposNextThreeMonths(dispos);

    return (
        <div className="mx-auto max-w-6xl px-6 py-10" data-testid="page-formateur-dashboard">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-orange">Espace formateur</div>
                    <h1 className="mt-3 font-display text-5xl font-normal tracking-tightest">
                        Bonjour {user?.first_name}.
                    </h1>
                    <p className="mt-3 max-w-xl text-brand-smoke">
                        Bienvenue dans votre espace Cursus Physio. Gardez vos informations à jour pour
                        recevoir les meilleures propositions de formation.
                    </p>
                </div>
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-orange text-white font-display text-2xl shadow-lg shadow-brand-orange/20">
                    {(user?.first_name || "?")[0]?.toUpperCase()}
                </div>
            </div>

            {!hasNear && !loading && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-8 flex items-start gap-3 rounded-2xl border border-brand-orange/40 bg-brand-orange/10 px-5 py-4 text-sm text-brand-ink"
                    data-testid="dispo-warning-banner"
                >
                    <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-orange" />
                    <div>
                        <div className="font-semibold">Aucune disponibilité sur les 3 prochains mois</div>
                        <div className="mt-1 text-brand-ink/80">
                            Merci de renseigner vos disponibilités pour que nous puissions vous proposer des sessions.
                        </div>
                        <Link
                            to="/formateur/disponibilites"
                            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"
                        >
                            Renseigner mes disponibilités <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </motion.div>
            )}

            {!summary?.has_public_profile && !loading && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 flex items-start gap-3 rounded-2xl border border-brand-ink/20 bg-brand-sand px-5 py-4 text-sm text-brand-ink"
                    data-testid="profile-incomplete-banner"
                >
                    <UserCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-orange" />
                    <div>
                        <div className="font-semibold">Complétez votre profil public</div>
                        <div className="mt-1 text-brand-ink/80">
                            Ces informations s'afficheront sur les pages de formations et cursus où vous intervenez.
                        </div>
                        <Link
                            to="/formateur/profil-public"
                            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"
                        >
                            Compléter mon profil <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </motion.div>
            )}

            {/* Legacy banner — formateur déjà onboardé sans formations_taught */}
            {!loading && summary && (summary.formations_taught_count || 0) === 0 && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 flex items-start gap-3 rounded-2xl border border-brand-orange/40 bg-brand-orange/10 px-5 py-4 text-sm text-brand-ink"
                    data-testid="formations-taught-legacy-banner"
                >
                    <ListChecks className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-orange" />
                    <div>
                        <div className="font-semibold">🎯 Nouveau · indiquez vos formations enseignables</div>
                        <div className="mt-1 text-brand-ink/80">
                            Sélectionnez les formations que vous pouvez animer pour optimiser le matching automatique des sessions.
                        </div>
                        <Link
                            to="/formateur/formations"
                            data-testid="formations-taught-legacy-cta"
                            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"
                        >
                            Sélectionner mes formations <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </motion.div>
            )}

            {loading ? (
                <div className="mt-10 text-brand-smoke">Chargement…</div>
            ) : (
                <>
                    <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                        <Card
                            icon={UserCircle2}
                            title="Profil public"
                            value={summary?.has_public_profile ? "Publié" : "À compléter"}
                            subtitle="Photo, bio, qualifications"
                            to="/formateur/profil-public"
                            ctaLabel={summary?.has_public_profile ? "Modifier" : "Compléter"}
                            testId="card-profil-public"
                        />
                        <Card
                            icon={ListChecks}
                            title="Mes formations"
                            value={summary?.formations_taught_count ?? 0}
                            subtitle="Formation(s) enseignable(s)"
                            to="/formateur/formations"
                            ctaLabel={(summary?.formations_taught_count || 0) > 0 ? "Modifier" : "Sélectionner"}
                            testId="card-formations-taught"
                        />
                        <Card
                            icon={FileText}
                            title="Mon programme"
                            value={summary?.has_programme ? "Validé" : (summary?.programme_draft ? "Brouillon" : "À créer")}
                            subtitle="Trame complète de votre formation"
                            to="/formateur/programme"
                            ctaLabel={summary?.has_programme ? "Voir / modifier" : "Compléter"}
                            testId="card-programme"
                        />
                        <Card
                            icon={CalendarClock}
                            title="Disponibilités"
                            value={summary?.disponibilites_count ?? 0}
                            subtitle="Jour(s) déclaré(s)"
                            to="/formateur/disponibilites"
                            ctaLabel="Modifier"
                            testId="card-disponibilites"
                        />
                        <Card
                            icon={MapPin}
                            title="Villes d'intervention"
                            value={summary?.villes_count ?? 0}
                            subtitle="Ville(s) ajoutée(s)"
                            to="/formateur/villes"
                            ctaLabel="Modifier"
                            testId="card-villes"
                        />
                    </div>

                    <div className="mt-14 rounded-2xl border border-brand-line bg-white p-8 shadow-premium" data-testid="formateur-next-sessions">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <CalendarCheck className="h-5 w-5 text-brand-orange" />
                                <h3 className="font-display text-2xl">Mes prochaines sessions</h3>
                            </div>
                            <Link to="/formateur/sessions" className="text-xs font-semibold text-brand-orange hover:underline">
                                Voir toutes →
                            </Link>
                        </div>
                        <UpcomingSessions />
                    </div>

                    <FormateurBadgeSection user={user} />
                </>
            )}
        </div>
    );
}

function UpcomingSessions() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/formateur/sessions?when=upcoming")
            .then((r) => setSessions(r.data.slice(0, 5)))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);
    if (loading) return <div className="mt-6 text-sm text-brand-smoke">Chargement…</div>;
    if (sessions.length === 0) {
        return (
            <div className="mt-6 rounded-xl border border-dashed border-brand-line bg-[#FAFAFA] p-10 text-center text-sm text-brand-smoke">
                Aucune session à venir pour le moment.
            </div>
        );
    }
    return (
        <div className="mt-5 space-y-2">
            {sessions.map((s) => (
                <Link
                    key={s.id}
                    to={`/formateur/sessions/${s.id}`}
                    data-testid={`dash-session-${s.id}`}
                    className="flex items-center justify-between rounded-xl border border-brand-line bg-white px-4 py-3 hover:border-brand-orange"
                >
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">
                            {new Date(s.start_date).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}
                        </div>
                        <div className="font-medium text-brand-ink">{s.formation_name}</div>
                        <div className="text-xs text-brand-smoke">{s.city} · {s.enrollments_count}/{s.total_seats} stagiaires</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-brand-orange" />
                </Link>
            ))}
        </div>
    );
}

function FormateurBadgeSection({ user }) {
    const [copied, setCopied] = useState(false);
    if (!user?.id) return null;
    const badgeUrl = `${API}/formateurs/${user.id}/badge.svg`;
    const embed = `<a href="https://cursus-physio.fr" target="_blank" rel="noopener"><img src="${badgeUrl}" alt="Formateur certifié Cursus Physio" /></a>`;

    const copy = async (txt) => {
        try {
            await navigator.clipboard.writeText(txt);
            setCopied(true);
            toast.success("Copié dans le presse-papier");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Copie impossible");
        }
    };

    return (
        <div className="mt-12 rounded-2xl border border-brand-line bg-brand-coal p-8 text-white shadow-premium" data-testid="formateur-badge-section">
            <div className="grid items-center gap-8 md:grid-cols-2">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">À partager</div>
                    <h3 className="mt-2 font-display text-3xl">Mon badge formateur</h3>
                    <p className="mt-3 text-sm text-white/70">
                        Affichez votre certification Cursus Physio sur votre site, votre signature email ou LinkedIn.
                        Le badge se met à jour automatiquement avec vos statistiques (sessions animées, satisfaction).
                    </p>
                    <div className="mt-5 space-y-2">
                        <button
                            onClick={() => copy(badgeUrl)}
                            data-testid="badge-copy-url"
                            className="inline-flex w-full items-center justify-between gap-2 rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-left text-xs hover:border-brand-orange"
                        >
                            <span className="truncate text-white/70">{badgeUrl}</span>
                            <span className="text-brand-orange font-semibold">{copied ? "✓ Copié" : "Copier l'URL"}</span>
                        </button>
                        <button
                            onClick={() => copy(embed)}
                            data-testid="badge-copy-embed"
                            className="inline-flex w-full items-center justify-between gap-2 rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-left text-xs hover:border-brand-orange"
                        >
                            <span className="truncate text-white/70">{`<a href="cursus-physio.fr"><img src="…" /></a>`}</span>
                            <span className="text-brand-orange font-semibold">Copier l'embed HTML</span>
                        </button>
                    </div>
                </div>
                <div className="flex justify-center">
                    <img
                        src={badgeUrl}
                        alt="Mon badge Cursus Physio"
                        data-testid="formateur-badge-preview"
                        className="rounded-2xl shadow-2xl"
                    />
                </div>
            </div>
        </div>
    );
}
